/**
 * What an intake record IS relative to the pipeline — the one question the
 * detail pane's action row answers.
 *
 * It used to know two states: converted ("Deal created as …") or not (Create
 * Deal). A reply whose revision was carried onto its deal is converted too, and
 * was announced as having created the deal; a reply whose revision was waiting
 * on a person was offered as a brand-new request with a Create Deal button — an
 * invitation to a duplicate deal (stress test LPR-24 / CRE-10).
 */
import type { ParseReport, UploadResult } from '@/services/ingestion'

/** The hold the server writes on mail read from the mailbox's Junk folder. */
const JUNK_HOLD = 'filed as Junk by the mail server'

export type RecordDisposition =
  | { kind: 'deal_created'; loanId: string }
  | { kind: 'filed_on_deal'; loanId: string; how: 'revision' | 'reconciled' }
  /** `lenderDraft`: every waiting value is from the lender's own draft terms
   *  (the firm's staff wrote it), not a revision from the broker (nd-039). */
  | { kind: 'revision_pending'; loanId: string; count: number; lenderDraft?: true }
  /** A revision linked to a deal that has since been killed: nothing may be
   *  applied to the dead record; reopen it or create a new deal. */
  | { kind: 'revision_on_killed_deal'; loanId: string; count: number }
  | { kind: 'held_against_deal'; loanId: string }
  | { kind: 'open' }

export function recordDisposition(record: {
  status?: string
  createdDealId?: string | null
  parseReport?: ParseReport | null
}): RecordDisposition {
  const report = record.parseReport ?? null
  const threadLoan = report?.thread?.loanUpdates?.loanId ?? null
  const reingest = report?.reingest ?? null
  if (record.status === 'converted' && record.createdDealId) {
    if (threadLoan && threadLoan === record.createdDealId) {
      return { kind: 'filed_on_deal', loanId: record.createdDealId, how: 'revision' }
    }
    if (reingest && reingest.status === 'applied' && reingest.loanId === record.createdDealId) {
      return { kind: 'filed_on_deal', loanId: record.createdDealId, how: 'reconciled' }
    }
    return { kind: 'deal_created', loanId: record.createdDealId }
  }
  const pending = report?.pendingRevisions ?? []
  const holdLoan = report?.promotionHold?.loanId ?? null
  if (pending.length > 0) {
    const loanId = holdLoan ?? threadLoan
    const killed = !!(report?.promotionHold?.loanKilled || report?.thread?.loanUpdates?.loanKilled)
    if (loanId && killed) return { kind: 'revision_on_killed_deal', loanId, count: pending.length }
    if (loanId) {
      const lenderDraft = pending.every((p) => p.lenderDraft)
      return { kind: 'revision_pending', loanId, count: pending.length, ...(lenderDraft ? { lenderDraft } : {}) }
    }
  }
  if (holdLoan) return { kind: 'held_against_deal', loanId: holdLoan }
  return { kind: 'open' }
}

/**
 * The deal this email became when a person made it its own deal, instead of a
 * revision of the deal its thread was linked to — or null.
 *
 * "This is a separate deal — create it" (nd-008: Juno Square / Keep Safe
 * Storage at one address) converts the email with force_new_loan. Its Email
 * Thread panel went on offering "Apply 2 selected to <first deal>", and
 * applying renamed the first deal after the second. The server now marks the
 * updates `separated`; a record separated before that is known by converting
 * to a different deal than the one its updates target.
 */
export function threadSeparatedAs(
  updates: { loanId: string; status?: string; separatedAs?: string | null } | null | undefined,
  createdDealId: string | null | undefined,
): string | null {
  if (!updates) return null
  if (updates.status === 'separated') return updates.separatedAs ?? createdDealId ?? null
  return createdDealId && createdDealId !== updates.loanId ? createdDealId : null
}

/**
 * What the Incoming Requests card says in place of "NEW · $X financing req."
 * when the record is not a new request at all. A waiting revision was carded
 * exactly like a fresh deal — the detail pane knew better, the list did not
 * (verifier, LPR-24). Null keeps the ordinary card.
 */
export function queueCardSummary(record: {
  status?: string
  createdDealId?: string | null
  parseReport?: ParseReport | null
}): { statusLabel: string; line: string } | null {
  const d = recordDisposition(record)
  if (d.kind === 'revision_pending' && d.lenderDraft) {
    // The desk's own proposal is not the broker revising the deal.
    return {
      statusLabel: 'Lender draft',
      line: `Lender draft terms for ${d.loanId} · ${d.count} value${d.count === 1 ? '' : 's'} waiting`,
    }
  }
  if (d.kind === 'revision_pending') {
    return {
      statusLabel: 'Revision',
      line: `Revision to ${d.loanId} · ${d.count} value${d.count === 1 ? '' : 's'} waiting`,
    }
  }
  if (d.kind === 'revision_on_killed_deal') {
    return { statusLabel: 'Revision', line: `Revises killed deal ${d.loanId}` }
  }
  // Mail Exchange filed as Junk is never promoted automatically; its card was
  // an ordinary NEW request that nobody was told was waiting on them (C17).
  if (d.kind === 'open' && record.parseReport?.promotionHold?.reason === JUNK_HOLD) {
    return { statusLabel: 'Junk', line: 'Filed as Junk by the mail server · convert by hand if it is a deal' }
  }
  // Not yet a deal, and it matches one the desk killed: the card says which,
  // so converting it is a knowing choice. A reply on a killed deal's thread was
  // carded "NEW … $16.0M financing req." with no word of the dead deal (nd-037).
  const killed = d.kind === 'open' ? record.parseReport?.closedDealMatches?.[0] : undefined
  if (killed) {
    return {
      statusLabel: 'New',
      line: killed.basis === 'same email thread'
        ? `Reply on killed deal ${killed.loanId}'s thread`
        : `Matches killed deal ${killed.loanId}`,
    }
  }
  return null
}

/**
 * What the upload screen says about an identical re-upload. It said only that
 * the content was already ingested — not that the deal it became is Dead
 * (C18/C30), so a re-sent package for a killed deal read as merely a repeat.
 */
export function duplicateUploadNotice(entry: Pick<UploadResult, 'duplicate' | 'record' | 'matchedDeal'>): string | null {
  if (!entry.duplicate) return null
  const dead = entry.matchedDeal
  if (dead) {
    const name = dead.propertyName ? ` (${dead.propertyName})` : ''
    return `Identical content was already ingested as ${entry.record.id}, which became deal ${dead.loanId}${name} — that deal is Dead. Nothing new was created. Reopen ${dead.loanId} if the deal is coming back, or archive the old record and re-parse to start a new request.`
  }
  return `Identical content was already ingested — showing the existing record (${entry.record.id}). Nothing was duplicated. If that record is stale (parsed by an older engine), archive it and re-parse this upload fresh.`
}
